/** @format */

import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { MapsData, Order } from "../../../services";

import { toast } from "react-toastify";
import moment from "moment/moment";

import { requestErrorDisplay } from "../../../utils";
import OrdersCreateDesktopView from "../../../components/orders/views/OrdersCreateDesktopView";
import { t } from "i18next";
import { useLanguage } from "../../../context/LanguageContext";
import { WEIGHT_UNITS } from "../../../mock";
import { getCitiesRefreshTargets } from "../utils/cityRefresh";
import { buildExistingOrderPayload } from "../utils/existingOrderPayload";
import { prepareOrderDocuments, saveOrderDocuments } from "../utils/existingOrderDocuments";
import { navigateAfterOrderEdit } from "../utils/orderEditNavigation";
import { RAILWAY_ORDERS_PATH } from "../utils/railwayOrder";

const RAILWAY_SHIPMENT_TYPES = new Set(["railway_shipment", "railway_shipment_40"]);

const ExistingOrderForm = ({ mode, initialForm, onDraftSaved }) => {
  const isCopy = mode === "copy";
  const isDraft = mode === "draft";
  const isRailway = Boolean(initialForm.isRailway);

  const { currentLanguage } = useLanguage();
  const user = useSelector((state) => state.user);
  const navigate = useNavigate();
  const location = useLocation();
  const submitting = useRef(false);

  const [recipientUpdate, setRecipientUpdate] = useState(0);

  // Default data

  const [countries] = useState(initialForm.countries);
  const [categories, setCategories] = useState(initialForm.categories);
  const [types] = useState(initialForm.types);
  const [temperatures] = useState(initialForm.temperatures);
  const [loadingTypes] = useState(initialForm.loadingTypes);
  const [partners] = useState(initialForm.partners);

  const [errors, setErrors] = useState({});
  // FORM
  // Selected data
  const [countryFrom, setCountryFrom] = useState(initialForm.countryFrom);
  const [countryTo, setCountryTo] = useState(initialForm.countryTo);
  const [citiesFrom, setCitiesFrom] = useState(initialForm.citiesFrom);
  const [citiesTo, setCitiesTo] = useState(initialForm.citiesTo);
  const [cityFrom, setCityFrom] = useState(initialForm.cityFrom);
  const [cityTo, setCityTo] = useState(initialForm.cityTo);
  const [pickUpDate, setPickUpDate] = useState(isCopy ? null : initialForm.pickUpDate);
  const [shippingDateTo, setShippingDateTo] = useState(isCopy ? null : initialForm.shippingDateTo);
  const [pickUpStartTime, setPickUpStartTime] = useState(isCopy ? null : initialForm.pickUpStartTime);
  const [pickUpEndTime, setPickUpEndTime] = useState(isCopy ? null : initialForm.pickUpEndTime);
  const [deliveryDate, setDeliveryDate] = useState(isCopy ? null : initialForm.deliveryDate);
  const [deliveryStartTime, setDeliveryStartTime] = useState(isCopy ? null : initialForm.deliveryStartTime);
  const [deliveryEndTime, setDeliveryEndTime] = useState(isCopy ? null : initialForm.deliveryEndTime);
  const [addressTo, setAddressTo] = useState(initialForm.addressTo);
  const [addressFrom, setAddressFrom] = useState(initialForm.addressFrom);
  const [coordinatesFrom, setCoordinatesFrom] = useState(initialForm.coordinatesFrom);
  const [coordinatesTo, setCoordinatesTo] = useState(initialForm.coordinatesTo);
  const [addressIdFrom, setAddressIdFrom] = useState(initialForm.addressIdFrom);
  const [addressIdTo, setAddressIdTo] = useState(initialForm.addressIdTo);
  const [recipientName, setRecipientName] = useState(initialForm.recipientName);
  const [recipientEmail, setRecipientEmail] = useState(initialForm.recipientEmail);
  const [recipientPhone, setRecipientPhone] = useState(initialForm.recipientPhone);
  const [senderName, setSenderName] = useState(initialForm.senderName);
  const [senderPhone, setSenderPhone] = useState(initialForm.senderPhone);
  const [category, setCategory] = useState(initialForm.category);
  const [type, setType] = useState(initialForm.type);
  const [wagonType, setWagonType] = useState(initialForm.wagonType);
  const [wagonsCount, setWagonsCount] = useState(
    isRailway && initialForm.isComplexTender ? "" : initialForm.wagonsCount ?? "",
  );
  const [etsngCode, setEtsngCode] = useState(initialForm.etsngCode ?? "");
  const [gngCode, setGngCode] = useState(initialForm.gngCode ?? "");
  const [weight, setWeight] = useState(initialForm.weight);
  const [volume, setVolume] = useState(initialForm.volume);
  const [pallet, setPallet] = useState(initialForm.pallet);
  const [tnvedCode, setTnvedCode] = useState(initialForm.tnvedCode);
  const [weightUnit, setWeightUnit] = useState(() => initialForm?.weightUnit ?? ({ ...WEIGHT_UNITS(t).ton }));
  const [details, setDetails] = useState(initialForm.details);
  const [copyCount, setCopyCount] = useState(1);
  const [price, setPrice] = useState(initialForm.price);
  const [algoPrice, setAlgoPrice] = useState(initialForm.algoPrice);
  const [predictedPrice, setPredictedPrice] = useState({});

  const [netPrice, setNetPrice] = useState(initialForm.netPrice);
  const [distance, setDistance] = useState(initialForm.distance);
  const [temperature, setTemperature] = useState(initialForm.temperature);
  const [loadingType, setLoadingType] = useState(initialForm.loadingType);
  const [createLoading, setCreateLoading] = useState(false);
  const [insurance, setInsurance] = useState(isCopy || isRailway ? false : initialForm.insurance);
  const [declaredPrice, setDeclaredPrice] = useState(isCopy ? 0 : initialForm.declaredPrice);
  const [insurancePrice, setInsurancePrice] = useState(isCopy ? null : initialForm.insurancePrice);
  const [minPickDate] = useState(new Date(new Date().setHours(0)));
  const [minDeliveryDate] = useState();
  const [partner, setPartner] = useState(isCopy ? null : initialForm.partner);
  const [assignmentMethod, updateAssignmentMethod] = useState(initialForm.assignmentMethod);
  const isTender = assignmentMethod !== "DirectAssignment";
  const [isComplexTender, updateIsComplexTender] = useState(initialForm.isComplexTender);
  const setIsComplexTender = (value) => {
    if (mode === "edit") return;
    updateIsComplexTender(value);
    if (isRailway && value) setWagonsCount("");
  };
  const [vehiclesCount, setVehiclesCount] = useState(initialForm.vehiclesCount);
  const [incotermsEnabled, setIncotermsEnabled] = useState(initialForm.incotermsEnabled);
  const [incoterms, setIncoterms] = useState(initialForm.incoterms);
  useEffect(() => {
    if (!countryFrom?.id || !countryTo?.id || countryFrom.id === countryTo.id) {
      setIncoterms(null);
      setIncotermsEnabled(false);
    }
  }, [countryFrom?.id, countryTo?.id]);

  const [auctionStep, setAuctionStep] = useState(initialForm.auctionStep ?? "");
  const [extendAuction, setExtendAuction] = useState(initialForm.extendAuction ?? false);
  const [isWithoutExecutor, setIsWithoutExecutor] = useState(isRailway || initialForm.isWithoutExecutor);
  const [tenderDateEnd, setTenderDateEnd] = useState(isCopy ? null : initialForm.tenderDateEnd);
  const [tenderEndTime, setTenderEndTime] = useState(isCopy ? null : initialForm.tenderEndTime);
  const [partnersForTender, setPartnersForTender] = useState(isCopy ? [] : initialForm.partnersForTender);
  const [points, setPoints] = useState(() => isCopy ? initialForm.points.map((point) => ({
    ...point,
    shippingDate: null,
    shippingHourFrom: null,
    shippingHourTo: null,
    expectedUnloadingDate: null,
  })) : initialForm.points);
  const [routeDistance, setRouteDistance] = useState(initialForm.routeDistance);
  const [, setRouteDuration] = useState("");
  const [currency, setCurrency] = useState(isCopy ? null : initialForm.currency);
  const [acceptedCurrency, setAcceptedCurrency] = useState(
    isCopy ? null : initialForm.acceptedCurrency,
  );

  const setAssignmentMethod = (method) => {
    if (mode === "edit") return;
    updateAssignmentMethod(method);
    if (method === "DirectAssignment") setIsComplexTender(false);
  };

  const [citiesRefreshCountryId, setCitiesRefreshCountryId] = useState(null);

  const [isCalculatePrice, setIsCalculatePrice] = useState(true);

  // files
  const [files, setFiles] = useState([]);
  const [serverFiles, setServerFiles] = useState(isCopy ? [] : initialForm?.serverFiles ?? []);
  const [senderEmail, setSenderEmail] = useState(initialForm?.senderEmail ?? "");
  const [fileInvoice, setInvoiceFile] = useState(isCopy ? null : initialForm.fileInvoice);

  //map
  const [isLoadingMap, setIsLoadingMap] = useState(false);
  const [routeData, setRouteData] = useState(null);
  const [routeStartCity, setRouteStartCity] = useState(null);

  const updatePrice = (newPrice) => {
    setPrice(newPrice);
  };

  const onResetPrice = () => {
    setAlgoPrice(0);
    setNetPrice(0);
    setDistance(0);
  };

  useEffect(() => {
    if (RAILWAY_SHIPMENT_TYPES.has(type?.value)) {
      setIsWithoutExecutor(true);
    }
  }, [type?.value]);

  useEffect(() => {
    if (
      points.length === 0 &&
      cityFrom &&
      countryFrom &&
      cityTo &&
      countryTo &&
      category &&
      type &&
      weight &&
      volume &&
      routeDistance
    ) {
      orderCalculation();
    } else if (points.length !== 0) {
      setAlgoPrice(0);
    }
  }, [points]);

  useEffect(() => {
    if (!isRailway && cityFrom?.id && cityTo?.id) {
      Order.getCalculateOrderPrice(cityFrom.id, cityTo.id)
        .then((response) => {
          setIsCalculatePrice(response.data);
        })
        .catch((error) => {

        });
    }
  }, [cityFrom, cityTo, isRailway]);

  useEffect(() => {
    if (mode === "edit") return;
    const timeoutId = setTimeout(() => {
      if (insurance && declaredPrice) {
        Order.calculateInsuranceSum(declaredPrice)
          .then((response) => {
            if (response?.data?.insuranceSum) {
              setInsurancePrice(response.data.insuranceSum);
            }
          })
          .catch((error) => {
            console.error('Error calculating insurance:', error);
            setInsurancePrice(null);
          });
      } else if (!declaredPrice || !insurance) {
        setInsurancePrice(null);
      }
    }, 800);

    return () => clearTimeout(timeoutId);
  }, [insurance, declaredPrice, price, mode]);

  useEffect(() => {
    if (citiesRefreshCountryId) {
      const responseEvent = (cities) => {
        const targets = getCitiesRefreshTargets(citiesRefreshCountryId, countryFrom, countryTo);

        if (targets.from) setCitiesFrom(cities);
        if (targets.to) setCitiesTo(cities);
      };

      Order.getCities(currentLanguage, citiesRefreshCountryId)
        .then((res) => res.data)
        .then((data) => {
          const cities = data?.filter(city => city.name).sort((a, b) => a.name.localeCompare(b.name));
          responseEvent(cities);
        })
        .catch((error) => {
          requestErrorDisplay(error);
        })
        .finally(() => {
          setCitiesRefreshCountryId(null);
        });
    }
  }, [citiesRefreshCountryId]);

  useEffect(() => {
    const responseEvent = (cities) => {
      setCitiesFrom(cities);
    };

    if (citiesTo.length && countryFrom?.id && countryTo?.id === countryFrom.id) {
      responseEvent(citiesTo);
    } else if (countryFrom?.id) {
      Order.getCities(currentLanguage, countryFrom.id)
        .then((res) => res.data)
        .then((data) => {
          const cities = data?.filter(city => city.name).sort((a, b) => a.name.localeCompare(b.name));

          responseEvent(cities);
        })
        .catch((error) => {
          requestErrorDisplay(error);
        });
    }
  }, [countryFrom]);

  useEffect(() => {
    const responseEvent = (cities) => {
      setCitiesTo(cities);
    };
    if (citiesFrom.length && countryTo?.id && countryFrom?.id === countryTo.id) {
      responseEvent(citiesFrom);
    } else if (countryTo?.id) {
      Order.getCities(currentLanguage, countryTo.id)
        .then((res) => res.data)
        .then((data) => {
          const cities = data?.filter(city => city.name).sort((a, b) => a.name.localeCompare(b.name));

          responseEvent(cities);
        })
        .catch((error) => {
          requestErrorDisplay(error);
        });
    }
  }, [countryTo]);

  useEffect(() => {
    setIsLoadingMap(true);

    const isAddressRoute = addressIdFrom && addressIdTo && coordinatesFrom && coordinatesTo;
    const routeMethod = isAddressRoute ? "getRouteByAddressId" : "getRoute";
    const routeParams = isAddressRoute
      ? [addressIdFrom, addressIdTo]
      : [cityFrom?.id, cityTo?.id];

    if ((isAddressRoute && addressIdFrom && addressIdTo) || (!isAddressRoute && cityFrom?.id && cityTo?.id)) {
      MapsData[routeMethod](...routeParams)
        .then((response) => {
          try {
            if (response) setRouteDistance(response.data.distance);
            let parsedData = JSON.parse(response.data.jsonData);

            if (
              (isAddressRoute && response.data.fromCityId !== addressIdFrom) ||
              (!isAddressRoute && response.data.fromCityId !== cityFrom?.id)
            ) {
              parsedData = parsedData.reverse();
            }

            if (response) {
              setRouteStartCity(response.data.fromCityId);
              setRouteData(parsedData);
            } else {
              setRouteData(null);

            }
            setIsLoadingMap(false);
          } catch (error) {
            console.error("Ошибка при парсинге JSON:", error);
            setRouteData(null);
            setIsLoadingMap(false);
          }
        })
        .catch((error) => {
          console.error(error);
          setIsLoadingMap(true);
          toast.error("Произошла ошибка, попробуйте позже");
        });
    }
  }, [cityFrom, cityTo, addressIdFrom, addressIdTo, coordinatesFrom, coordinatesTo]);

  const orderCalculation = () => {
    if (isRailway || points.length !== 0) {
      return;
    }

    const data = {
      countryFrom: `${countryFrom?.name}`,
      countryTo: `${countryTo?.name}`,
      weight: weightUnit.key === "kg" ? String(weight/1000).replace(",", ".") : String(weight).replace(",", "."),
      volume: String(volume).replace(",", "."),
      transportType: type.value,
      cargoCategory: category.name,
      loadingDate: pickUpDate ? moment(pickUpDate).format("YYYY-MM-DD") : null,
      distance: routeDistance ?  Math.round(routeDistance) : 0,
    };

    Order.getOrderPrice(data)
      .then((response) => {
        if (response.status === 200) {
          if (pickUpDate) {
            setAlgoPrice(response.data.predicted_price);
            setPredictedPrice({});
          } else {
            setPredictedPrice(response.data.predicted_price);
            setAlgoPrice(0);
          }
        } else {

          toast.error("[Calculation] - Произошла ошибка, попробуйте позже");
        }
      })
      .catch((error) => {

      });
  };

  const queryInsurance = (nextValue = !insurance) => {
    if (mode === "edit") return;
    setInsurance(nextValue);
    if (!nextValue) {
      setDeclaredPrice(0);
      setInsurancePrice(null);
      setInvoiceFile({});
    }
  };

  const saveDraft = async () => {
    if (submitting.current) return;
    submitting.current = true;
    setCreateLoading(true);
    try {
      const data = buildExistingOrderPayload(getPropsData(), initialForm.source, "draft", user?.id);
      await Order.updateDraft(data, initialForm.draftId);
      toast.success(t("ordersCreate.toast.savedToDrafts"));
      onDraftSaved?.();
    } catch (error) {
      requestErrorDisplay(error, t, "ordersCreate.errors.createDraftError");
    } finally {
      submitting.current = false;
      setCreateLoading(false);
    }
  };

  const createOrder = async () => {
    if (submitting.current) return;
    submitting.current = true;
    setCreateLoading(true);
    const paymentWindow = insurance && user?.insuranceType !== "GeneralAgreement"
      ? window.open("", "_blank") : null;
    try {
      const data = buildExistingOrderPayload(getPropsData(), initialForm.source, mode, user?.id);
      const documents = isDraft ? [] : await prepareOrderDocuments({ files, insurance, fileInvoice });
      const response = mode === "edit"
        ? await Order.newUpdate(initialForm.source.order.id, data)
        : await Order.newCreate(data);
      const result = response.data;
      const ids = mode === "edit" ? [initialForm.source.order.id]
        : (result?.orders ?? (Array.isArray(result) ? result : result?.id ? [result] : []))
          .map((order) => typeof order === "string" ? order : order.id);
      const paymentUrl = result?.insurance?.paymentUrl;
      if (paymentWindow && paymentUrl) paymentWindow.location.href = paymentUrl;
      else paymentWindow?.close();

      const removedFileIds = mode === "edit" ? (initialForm.serverFiles ?? [])
        .filter((file) => !serverFiles.some((retained) => retained.fileId === file.fileId))
        .map((file) => file.fileId) : [];
      if (mode === "edit" && initialForm.fileInvoice?.fileId &&
        (!insurance || fileInvoice?.fileId !== initialForm.fileInvoice.fileId)) {
        removedFileIds.push(initialForm.fileInvoice.fileId);
      }
      // The order is already saved: a document error must not trigger duplicate creation on retry.
      toast.success(t(mode === "edit" ? "ordersCopy.toast.orderEdited" : "ordersCreate.toast.orderCreated"));
      try {
        if (!isDraft) await saveOrderDocuments(ids, documents, removedFileIds);
      } catch (error) {
        requestErrorDisplay(error, t, "ordersCreate.errors.uploadFileError");
      }
      const listPath = isRailway ? RAILWAY_ORDERS_PATH : "/main";
      if (mode === "edit") navigateAfterOrderEdit(navigate, location.state, listPath);
      else navigate(listPath);
    } catch (error) {
      paymentWindow?.close();
      requestErrorDisplay(error, t);
    } finally {
      submitting.current = false;
      setCreateLoading(false);
    }
  };

  const getPropsData = () => ({
    isRailway,
    wagonTypes: initialForm.wagonTypes ?? [],
    wagonType,
    setWagonType,
    wagonsCount,
    setWagonsCount,
    etsngCode,
    setEtsngCode,
    gngCode,
    setGngCode,
    createOrder,
    saveDraft: isDraft ? saveDraft : undefined,
    assignmentMethod,
    setAssignmentMethod,
    isComplexTender,
    setIsComplexTender,
    vehiclesCount,
    setVehiclesCount,
    incotermsEnabled,
    setIncotermsEnabled,
    incoterms,
    setIncoterms,
    auctionStep,
    setAuctionStep,
    extendAuction,
    setExtendAuction,
    setCountryFrom,
    setCityFrom,
    countries,
    countryFrom,
    citiesFrom,
    cityFrom,
    countryTo,
    setCountryTo,
    setCityTo,
    citiesTo,
    cityTo,
    pickUpDate,
    shippingDateTo,
    setShippingDateTo,
    pickUpStartTime,
    pickUpEndTime,
    minPickDate,
    setPickUpDate,
    setPickUpStartTime,
    setPickUpEndTime,
    deliveryDate,
    deliveryStartTime,
    deliveryEndTime,
    minDeliveryDate,
    setDeliveryDate,
    setDeliveryStartTime,
    setDeliveryEndTime,
    types,
    setType,
    setTemperature,
    type,
    addressTo,
    setAddressTo,
    setRecipientPhone,
    setRecipientName,
    recipientName,
    recipientPhone,
    recipientEmail,
    recipientUpdate,
    setRecipientEmail,
    addressFrom,
    setAddressFrom,
    setRecipientUpdate,
    volume,
    setVolume,
    weight,
    setWeight,
    weightUnit,
    setWeightUnit,
    pallet,
    setPallet,
    tnvedCode,
    setTnvedCode,
    categories,
    category,
    setCategory,
    temperatures,
    temperature,
    loadingTypes,
    loadingType,
    setLoadingType,
    details,
    setDetails,
    insurance,
    queryInsurance,
    declaredPrice,
    setDeclaredPrice: (value) => {
      if (mode !== "edit") setDeclaredPrice(value);
    },
    fileInvoice,
    setInvoiceFile: (value) => {
      if (mode !== "edit") setInvoiceFile(value);
    },
    files,
    setFiles,
    serverFiles,
    onDeleteServerFile: (fileId) => setServerFiles((items) => items.filter((item) => item.fileId !== fileId)),
    deferServerFileDeletion: true,
    distance,
    price,
    netPrice,
    algoPrice,
    setAlgoPrice,
    predictedPrice,
    setPredictedPrice,
    insurancePrice,
    createLoading,
    copyCount,
    setCopyCount: (value) => {
      if (mode !== "edit") setCopyCount(value);
    },
    orderCalculation,
    onResetPrice,
    updatePrice,
    setIsСallСities: setCitiesRefreshCountryId,
    isCalculatePrice,
    isLoadingMap,
    routeData,
    routeStartCity,
    partners,
    setPartner,
    partner,
    isTender,
    tenderDateEnd,
    tenderEndTime,
    setTenderDateEnd,
    setTenderEndTime,
    partnersForTender,
    setPartnersForTender,
    setCoordinatesFrom,
    setCoordinatesTo,
    coordinatesFrom,
    coordinatesTo,
    addressIdFrom,
    addressIdTo,
    setAddressIdFrom,
    setAddressIdTo,
    points,
    setPoints,
    senderName,
    setSenderName,
    senderEmail,
    setSenderEmail,
    setSenderPhone,
    senderPhone,
    routeDistance,
    setRouteDistance,
    setRouteDuration,
    currency,
    setCurrency,
    acceptedCurrency,
    setAcceptedCurrency,
    isWithoutExecutor,
    setIsWithoutExecutor,
  });

  return (
    <>
      <OrdersCreateDesktopView
        {...getPropsData()}
        isCopy={mode === "copy"}
        isEdit={mode === "edit"}
        headTitle={t(`ordersCopy.title.${mode}`)}
        onPriceChange={setPrice}
        errors={errors}
        setErrors={setErrors}
        setCategories={setCategories}
      />
    </>
  );
};

export default ExistingOrderForm;
